import { ReactNode, useState, useEffect } from 'react';
import * as Sentry from "@sentry/react";
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logging';

const ONBOARDING_EXEMPT_ROUTES = [
  '/login',
  '/register',
  '/register/verify',
  '/register/password',
  '/auth/complete-profile',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/terms',
  '/privacy',
  '/status',
];

interface AuthGuardProps {
  children: ReactNode;
  requireAuth?: boolean;
  requireVerification?: boolean;
}

export function AuthGuard({ 
  children, 
  requireAuth = true,
  requireVerification = true 
}: AuthGuardProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  const isExemptRoute = ONBOARDING_EXEMPT_ROUTES.some(
    route => location.pathname === route || location.pathname.startsWith(route + '/')
  );

  // Profile completion check — cached for the session, only runs when authenticated
  const { data: profileRole, isLoading: isProfileLoading } = useQuery({
    queryKey: ['auth-guard-profile-role', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) {
        logger.warn('AuthGuard profile role query failed', { 
          component: 'AuthGuard', 
          error: error.message 
        });
        return null;
      }
      // Treat missing row the same as null role — both mean onboarding incomplete
      if (!data) return null;
      return data.role ?? null;
    },
    enabled: !!user && !isExemptRoute,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

  // Add timeout for loading state to prevent infinite loading
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        logger.warn('AuthGuard loading timeout reached', { component: 'AuthGuard' });
        setLoadingTimeout(true);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timer);
    } else {
      setLoadingTimeout(false);
    }
  }, [loading]);

  if (loading && !loadingTimeout) {
    logger.debug('AuthGuard showing loading spinner', { component: 'AuthGuard' });
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-sm text-muted-foreground">Loading authentication...</p>
        </div>
      </div>
    );
  }

  if (loadingTimeout) {
    Sentry.captureException(new Error('[AuthGuard] Auth loading timeout'), {
      tags: { context: 'auth_error', component: 'AuthGuard' },
      extra: { message: 'Auth loading timeout, showing error' }
    });
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md">
          <h2 className="text-xl font-semibold text-destructive">Authentication Timeout</h2>
          <p className="text-sm text-muted-foreground">
            The authentication system is taking longer than expected. This might be a network issue.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  if (requireAuth && !user) {
    logger.securityEvent('Protected route access blocked (user not authenticated)', {
      component: 'AuthGuard',
      attemptedPath: location.pathname
    });
    
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!requireAuth && user) {
    const from = location.state?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  // Profile completion gate — only for authenticated, non-exempt routes
  if (requireAuth && user && !isExemptRoute) {
    if (isProfileLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-sm text-muted-foreground">Preparing your account...</p>
          </div>
        </div>
      );
    }

    if (!profileRole) {
      logger.info('AuthGuard redirecting to onboarding — profile role not set', {
        component: 'AuthGuard',
        userId: user.id,
        attemptedPath: location.pathname,
      });
      return <Navigate to="/auth/complete-profile" replace />;
    }
  }

  return <>{children}</>;
}